import React, { useState } from 'react';
import style from '../content-type.module.scss';
import { SimpleChoice as TSimpleChoice } from '../../types';
import moment from 'moment';
import { EditIcon, DeleteIcon } from '../../../../icons';
import { AddSimpleChoice } from './AddSimpleChoice';
import { useMutate, useAppAndBranchId, usePermissions } from '../../../../hooks';
import { api } from '../../api';
import { Modal } from '../../../../components';
import { useToast } from '../../../../../../../../helpers/commonFunction';
import { errorMessage } from '../../../../utils/error';

type Props = {
	simpleChoice: TSimpleChoice & { id: string; updatedAt:string|null; createdAt: string };
	refetchContent: () => void;
};

export function SimpleChoice(props: Props) {
	const { ErrorToast } = useToast()
	const [appId, branchId] = useAppAndBranchId();
	const { simpleChoice, refetchContent } = props;
	const { mutate: deleteMutate, loading: deleteLoading } = useMutate(
		api.deleteContent,
	);

	const [modalVisibility, setModalVisibility] = useState({
		editContent: false,
		viewContent: false,
		deleteContent: false,
	});

	const [canEdit, canDelete] = usePermissions(['content.Edit', 'content.Delete']) as boolean[]

	function updateVisibility(
		key: keyof typeof modalVisibility,
		visibility: boolean,
	) {
		setModalVisibility((prev) => ({
			...prev,
			[key]: visibility,
		}));
	}

	return (
		<div className={style.contentTypeWrapper}>
			<div className={style.contentType} data-has-action={canEdit || canDelete} onClick={() => {
					updateVisibility('viewContent', true)
				}}>
				<div className={style.left}>
					<p>{simpleChoice.contentValue.title}</p>
					<p><span>Number of Choices:</span> {simpleChoice.contentValue.choices.length}</p>
				</div>
				<div className={style.info}>
					<p>{simpleChoice.updatedAt ? moment(simpleChoice.updatedAt).format('DD MMM YYYY, hh:mm A') : "" }</p>
				</div>
				<div className={style.info}>
					<p>{moment(simpleChoice.createdAt).format('DD MMM YYYY, hh:mm A')}</p>
					<div className={style.actions}>
						{(canEdit || canDelete) && <div className={style.line} />}
						{canEdit && <button
							onClick={(e) => {
								e.stopPropagation()
								updateVisibility('editContent', true);
							}}
						>
							<EditIcon width="14" height="14" stroke="#141B34" />
						</button>
						}{canDelete &&
							<button
								onClick={async (e) => {
									e.stopPropagation()
									updateVisibility('deleteContent', true);
								}}
							>
								<DeleteIcon width="14" height="14" stroke="#D30000" />
							</button>
						}
					</div>
				</div>
			</div>
			{modalVisibility.viewContent && (
				<AddSimpleChoice
					mode="view"
					refetchContent={refetchContent}
					initialValue={{
						title: simpleChoice.contentValue.title,
						choices: simpleChoice.contentValue.choices as any,
						orientation: simpleChoice.contentValue.style.orientation,
						id: simpleChoice.id,
					}}
					isOpen={modalVisibility.viewContent}
					close={() => {
						updateVisibility('viewContent', false);
					}}
				/>
			)}
			{modalVisibility.editContent && (
				<AddSimpleChoice
					mode="edit"
					refetchContent={refetchContent}
					initialValue={{
						title: simpleChoice.contentValue.title,
						choices: simpleChoice.contentValue.choices as any,
						orientation: simpleChoice.contentValue.style.orientation,
						id: simpleChoice.id,
					}}
					isOpen={modalVisibility.editContent}
					close={() => updateVisibility('editContent', false)}
				/>
			)}
			<Modal
				styles={{
					'--ok-btn-bg': '#DE0000',
					'--ok-btn-filter': 'drop-shadow(0px 8px 13px rgba(211, 0, 73, 0.18))',
				}}
				okText="Delete"
				loading={deleteLoading}
				onOk={async () => {
					try { 
						await deleteMutate(appId, branchId, simpleChoice.id);
						refetchContent();
					} catch(err) {
					  ErrorToast(errorMessage("delete", "simple choice", err.code))
					}
				}}
				onCancel={() => updateVisibility('deleteContent', false)}
				isOpen={modalVisibility.deleteContent}
				title="Remove Chosen Simple Choice"
			>
				<div
					style={{
						padding: '18px 14px',
						color: '#393939',
						fontSize: 15,
						fontWeight: 500,
					}}
				>
					<p>Do you really want to remove the chosen simple choice?</p>
				</div>
			</Modal>
		</div>
	);
}
